import isEmpty from 'lodash/isEmpty';
import {
	createActionsHook,
	createHook,
	createStore,
	type StoreActionApi,
	type Action,
} from 'react-sweet-state';

import type { IEnvironment } from 'relay-runtime';
import { ff } from '@atlassian/jira-feature-flagging';
import { fireOperationalAnalyticsWithoutContext } from '@atlassian/jira-ui-modifications-analytics';
import {
	LOOKUP_SOURCE_ALLOWED_VALUES_PROPERTY,
	LOOKUP_SOURCE_USERS_ASYNC_DATA,
} from '@atlassian/jira-ui-modifications-fields-configuration/src/common/utils/common/constants';
import type {
	LookupData,
	FieldId,
	FieldMapFromIframe,
	FieldInternalShape,
} from '@atlassian/jira-ui-modifications-fields-configuration/src/types';

import type { TenantContext, ViewContext } from '@atlassian/jira-ui-modifications-types';
import {
	initializeField,
	processChange,
	setFieldValue,
	updateFields,
	successFieldsInitalisation,
	startFieldsInitialisationTimeout,
	setError,
	resetError,
	bufferChanges,
	bufferTimeout,
	handleBufferAllAppsError,
	sendBufferFinalMetrics,
	sendBufferConflictMetrics,
	initializeOnInitBuffer,
	clearAllBufferTimeouts,
	clearBufferForChangeId,
	addBufferError,
	resetBufferErrors,
	registerFields,
	unregisterFields,
	unregisterAllFieldsOfChangeId,
	processIframeChanges,
	setAppErrors,
	resetAppErrors,
	initializeScreenTabs,
	updateScreenTabs,
	updateActiveScreenTab,
} from './actions';
import type {
	AnalyticsData,
	AppId,
	ChangeId,
	IssueAdjustmentsState,
	StoreContainerProps,
} from './types';
import {
	transformFieldFromIframe,
	transformFieldToIframe,
	mapFieldPropsFromPublicToInternalShape,
	fieldWasRegisteredDeprecated,
	fieldWasRegistered,
} from './utils';

const name = 'issue-adjustments';
export const initialState: IssueAdjustmentsState = {
	formData: null,
	screenTabs: null,
	appliedChanges: null,
	error: null,
	analyticsData: {},
	registeredFieldsDeprecated: {},
	registeredFields: {},
	triggerFieldInitializationKey: 0,
	// Selector checks if this is not equal 0 in the useFieldReady
	numberOfSupportedFields: 0,
	internalFormMetadata: {},
	viewType: null,
	experienceDataIsLoading: false,
	buffer: {},
	bufferTimeouts: {},
	bufferErrors: null,
	lastProcessedChange: null,
	fieldsInitialization: {
		startTime: null,
		timeoutId: undefined,
		status: 'notStarted',
	},
	appsErrors: {},
};

// triggerFieldInitializationKey, viewType and numberOfSupportedFields should not be reset on project or issue type change
const {
	triggerFieldInitializationKey,
	viewType: scopedViewType,
	numberOfSupportedFields,
	...stateForResetAllFields
} = initialState;

const multiAppsBufferingActions = {
	bufferChanges,
	bufferTimeout,
	handleBufferAllAppsError,
	sendBufferFinalMetrics,
	sendBufferConflictMetrics,
	initializeOnInitBuffer,
	clearAllBufferTimeouts,
	clearBufferForChangeId,
	addBufferError,
	resetBufferErrors,
};

export const actions = {
	triggerFieldInitialization:
		() =>
		({ getState, setState }: StoreActionApi<IssueAdjustmentsState>) => {
			setState({
				triggerFieldInitializationKey: getState().triggerFieldInitializationKey + 1,
			});
		},
	processChange,
	setFieldValue,
	initializeField,
	startFieldsInitialisationTimeout,
	successFieldsInitalisation,
	...multiAppsBufferingActions,
	registerFields,
	unregisterFields,
	unregisterAllFieldsOfChangeId,
	processIframeChanges,
	// TODO: remove updateFieldsOld with forge-ui-modifications-validation-errors-returned-to-app_hdzmw clean up
	updateFieldsOld:
		({
			changeId,
			fieldsChanges,
			lookupDataFromApi,
			tenantContext,
			viewContext,
			environment,
			appId,
		}: {
			changeId: ChangeId;
			fieldsChanges: FieldMapFromIframe;
			lookupDataFromApi: Exclude<LookupData, typeof LOOKUP_SOURCE_ALLOWED_VALUES_PROPERTY>;
			tenantContext: TenantContext;
			viewContext: ViewContext;
			environment: IEnvironment;
			appId: AppId;
		}): Action<IssueAdjustmentsState, StoreContainerProps, Promise<void>> =>
		async ({ setState, getState }, { viewType }) => {
			const appliedChanges = getState().appliedChanges || {};
			const formData = getState().formData || {};
			const { internalFormMetadata, registeredFields, registeredFieldsDeprecated } = getState();
			const { changesToApply, incomingFormData } = await Object.keys(fieldsChanges).reduce(
				async (acc, fieldId: FieldId) => {
					// Do not update fields that are not inside initialized data.
					// Validates if the field that comes from Iframe is supported
					// - because we add to formData only the supported ones.
					const fieldData = formData[fieldId];
					const internalMetadata = internalFormMetadata[fieldId];
					const fieldIsRegistered = ff('ui-modifications-multi-app-experience_ud4yw')
						? fieldWasRegistered(registeredFields, changeId, fieldId, appId)
						: fieldWasRegisteredDeprecated(registeredFieldsDeprecated, changeId, fieldId);

					// Only registered field Ids can be updated.
					if (!fieldIsRegistered || !fieldData) {
						// eslint-disable-next-line no-console
						console.warn(`UIM tried to apply changes to the unregistered field ${fieldId}`);

						fireOperationalAnalyticsWithoutContext('applyChanges', 'unregisteredField', viewType, {
							message: 'Attempt to apply changes to a field that was not registered',
							fieldId,
						});

						return acc;
					}

					const lookupData: LookupData = {
						[LOOKUP_SOURCE_ALLOWED_VALUES_PROPERTY]: internalMetadata.allowedValues,
						[LOOKUP_SOURCE_USERS_ASYNC_DATA]: lookupDataFromApi[LOOKUP_SOURCE_USERS_ASYNC_DATA],
					};

					// It updates properties in each field to avoid removing unchanged props for
					// selected field by another app or another onChange update.
					// Only supported properties are allowed.

					const supportedFieldChanges = await transformFieldFromIframe({
						fieldType: fieldData.fieldType,
						fieldId,
						fieldFromIframe: fieldsChanges?.[fieldId],
						lookupData,
						viewType,
						tenantContext,
						viewContext,
						environment,
					});

					// It converts changes to 'public' format to update the formData
					const publicShapeChanges = transformFieldToIframe(
						supportedFieldChanges,
						viewType,
						fieldData.fieldType,
						lookupData,
						fieldsChanges?.[fieldId],
						internalMetadata,
					);

					const transformedFieldChanges = mapFieldPropsFromPublicToInternalShape(
						supportedFieldChanges,
						viewType,
						fieldData.fieldType,
					);

					const combinedChangesToApply = {
						...appliedChanges[fieldId],
						...transformedFieldChanges,
					};

					if (!isEmpty(combinedChangesToApply)) {
						(await acc).changesToApply[fieldId] = combinedChangesToApply;
						(await acc).incomingFormData[fieldId] = {
							...formData[fieldId],
							...publicShapeChanges,
						};
					}

					return acc;
				},
				Promise.resolve({
					changesToApply: { ...appliedChanges },
					incomingFormData: { ...formData },
				}),
			);

			setState({
				appliedChanges: changesToApply,
				formData: incomingFormData,
			});
		},
	updateFields,
	clearAppliedValue:
		(fieldId: FieldId) =>
		({ setState, getState }: StoreActionApi<IssueAdjustmentsState>) => {
			const previousState = getState().appliedChanges || {};

			if (previousState[fieldId]) {
				const { value, ...otherFieldChanges } = previousState[fieldId];

				setState({
					appliedChanges: {
						...previousState,
						[fieldId]: otherFieldChanges,
					},
				});
			}
		},
	resetAllFields:
		() =>
		({ setState, getState, dispatch }: StoreActionApi<IssueAdjustmentsState>) => {
			// Clear the fieldsInitialization timeout to avoid potetianl side effects.
			clearTimeout(getState().fieldsInitialization.timeoutId);

			if (ff('ui-modifications-multi-app-experience_ud4yw')) {
				dispatch(clearAllBufferTimeouts());
			}

			setState(stateForResetAllFields);
		},
	setError,
	resetError,
	setAnalyticsData:
		(analyticsData: Partial<AnalyticsData>) =>
		({ setState, getState }: StoreActionApi<IssueAdjustmentsState>) => {
			const previousState: Partial<AnalyticsData> = getState().analyticsData ?? {};

			setState({
				analyticsData: {
					...previousState,
					...analyticsData,
				},
			});
		},
	setNumberOfSupportedFields:
		(numberOfAllSupportedFields: number) =>
		({ setState }: StoreActionApi<IssueAdjustmentsState>) => {
			setState({ numberOfSupportedFields: numberOfAllSupportedFields });
		},
	/**
	 * This is used to block onInit for specific case like Issue View background refresh loading.
	 */
	setExperienceDataIsLoading:
		(experienceDataIsLoading: boolean) =>
		({ setState }: StoreActionApi<IssueAdjustmentsState>) => {
			setState({ experienceDataIsLoading });
		},
	setAppErrors,
	resetAppErrors,
	initializeScreenTabs,
	updateScreenTabs,
	updateActiveScreenTab,
};

export type Actions = typeof actions;
// It was required to satisfiy TS with the async action
export type HookActions = ReturnType<typeof useIssueAdjustmentsActions>;

export const store = createStore<IssueAdjustmentsState, Actions>({
	name,
	initialState,
	actions,
});

export const useIssueAdjustments = createHook(store);

const stableEmptyFieldFromIframe: FieldInternalShape = {};
export const useAdjustedField = createHook(store, {
	selector: (state: IssueAdjustmentsState, fieldId: FieldId) =>
		state.appliedChanges?.[fieldId] || stableEmptyFieldFromIframe,
});

export const useAnalyticsData = createHook(store, {
	selector: (state: IssueAdjustmentsState) => state.analyticsData,
});

export const useIssueAdjustmentsActions = createActionsHook(store);

export const useAdjustedScreenTabs = createHook(store, {
	selector: (state: IssueAdjustmentsState) => state.screenTabs,
});
